import React, { useEffect, useMemo, useState } from 'react';

import MerchantSelect from '@components/operator/MerchantSelect';
import InboundEditForm from '@components/shared/inbounds/InboundEdit';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorInboundService } from '@hooks/operator/inbounds/useOperatorInboundService';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useOperatorSupplierService } from '@hooks/operator/suppliers/useOperatorSupplierService';

import operatorInventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  [key: string]: unknown;
}

const OperatorInboundNew: React.FC = () => {
  const { operator } = useOperatorAuth();
  const operatorWarehouseName = (operator?.warehouse as { title?: string } | undefined)?.title;

  const { merchantInventoryTypes, currentMerchant } = useOperatorData();

  // 新增模式由 MerchantSelect 設定 currentMerchant；reference data 跟著 currentMerchant 走。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const allInventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const [inventoryAttributeDefinitions, setInventoryAttributeDefinitions] = useState<InventoryAttributeDefinition[]>([]);

  const supplierService = useOperatorSupplierService({ merchantId: effectiveMerchantId ?? undefined });

  useEffect(() => {
    if (!effectiveMerchantId) {
      setInventoryAttributeDefinitions([]);
      return;
    }
    let cancelled = false;
    operatorInventoryAttributeDefinitionService
      .fetchList({ merchant_id: effectiveMerchantId })
      .then((result: { success?: boolean; data?: InventoryAttributeDefinition[] }) => {
        if (cancelled) return;
        setInventoryAttributeDefinitions(result?.data ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to fetch inventory attribute definitions', error);
        setInventoryAttributeDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveMerchantId]);

  // 新增模式：先選擇 merchant
  return (
    <MerchantSelect>
      <InboundEditForm
        useInboundService={useOperatorInboundService}
        useProductService={useOperatorProductService}
        supplierService={supplierService}
        role="operator"
        operatorWarehouseName={operatorWarehouseName}
        inventoryTypes={inventoryTypes}
        allInventoryTypes={allInventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
      />
    </MerchantSelect>
  );
};

export default OperatorInboundNew;
